<template>
  <div class="work-select-form">
    <div v-if="categoryOptions.length" class="field">
      <label class="field-label" for="contest-entry-category">
        {{ t("contestEntryDataForm.category") }}
        <span class="required-mark" aria-hidden="true">*</span>
      </label>
      <div class="select-wrapper">
        <select
          id="contest-entry-category"
          :value="value.categoryId"
          class="field-input field-select"
          @change="onCategoryChange"
        >
          <option value="">{{ t("contestEntryDataForm.categoryPlaceholder") }}</option>
          <option v-for="option in categoryOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <IconChevronDown class="select-chevron" />
      </div>
    </div>

    <div class="submission-section">
      <h2 class="submission-title">{{ t("contestEntryForm.step2.submissionWork") }}</h2>

      <template v-if="hasSelectedNolaWork">
        <div class="selected-work-card">
          <ImageView class="selected-work-cover" :src="value.novelImage" />
          <p class="selected-work-title">{{ value.novelTitle }}</p>
          <button
            type="button"
            class="clear-work-button"
            :aria-label="t('contestEntryForm.step2.clearSelectedWork')"
            @click="clearSelectedWork"
          >
            <IconClose class="clear-work-icon" />
          </button>
        </div>

        <div class="field">
          <div class="field-header">
            <label class="field-label" for="contest-entry-work-title">
              {{ t("contestEntryForm.step2.workTitle.label") }}
              <span class="required-mark" aria-hidden="true">*</span>
            </label>
            <span class="field-count">{{ value.workTitle.length }}/100</span>
          </div>
          <input
            id="contest-entry-work-title"
            :value="value.workTitle"
            class="field-input"
            type="text"
            maxlength="100"
            @input="onWorkTitleInput"
          />
        </div>

        <div class="field synopsis-field">
          <div class="field-header">
            <label class="field-label" for="contest-entry-synopsis">
              {{ t("contestEntryForm.step2.synopsis.label") }}
              <span class="required-mark" aria-hidden="true">*</span>
            </label>
            <span class="field-count">{{ value.synopsis.length }}/1,000</span>
          </div>
          <textarea
            id="contest-entry-synopsis"
            :value="value.synopsis"
            class="field-input field-textarea"
            maxlength="1000"
            rows="5"
            :placeholder="t('contestEntryForm.step2.synopsis.placeholder')"
            @input="onSynopsisInput"
          />
        </div>

        <div class="field copy-synopsis-field">
          <ContestEntryCopySynopsisSelect @select="onCopySynopsisSelect" />
        </div>
      </template>

      <div v-else class="source-cards">
        <button
          type="button"
          class="source-card"
          :class="{ selected: value.workSource === 'nola' }"
          :disabled="isSourceSelectDisabled"
          @click="onClickSelectFromNola"
        >
          <IconNoteBook class="source-icon" />
          <span class="source-label">{{ t("contestEntryForm.step2.selectFromNola") }}</span>
        </button>
        <button type="button" class="source-card" :disabled="isSourceSelectDisabled" @click="onClickImportFromLocal">
          <IconUpload class="source-icon" />
          <span class="source-label">{{ t("contestEntryForm.step2.importFromLocal") }}</span>
        </button>
      </div>
    </div>

    <div v-if="extraFieldItems.length" class="extra-fields">
      <div v-for="(field, index) in extraFieldItems" :key="`${field.key}-${index}`" class="field">
        <div class="field-header">
          <label class="field-label" :for="`contest-entry-extra-${index}`">
            {{ field.title }}
            <span v-if="field.required" class="required-mark" aria-hidden="true">*</span>
          </label>
          <span v-if="extraFieldMaxLength(field) !== undefined" class="field-count">
            {{ extraAnswer(field.key).length }}/{{ field.maxLength }}
          </span>
        </div>
        <p v-if="field.description" class="field-description">{{ field.description }}</p>
        <input
          :id="`contest-entry-extra-${index}`"
          :value="extraAnswer(field.key)"
          class="field-input"
          type="text"
          :maxlength="extraFieldMaxLength(field)"
          @input="onExtraAnswerInput(field.key, $event)"
        />
      </div>
    </div>

    <div class="form-footer">
      <button type="button" class="back-button" @click="emit('back')">
        {{ t("contestEntryForm.step2.back") }}
      </button>
      <button type="button" class="next-button" :disabled="!canProceed" @click="emit('next')">
        {{ t("contestEntryForm.step2.next") }}
      </button>
    </div>

    <MaskedLoading v-if="isImporting" class="import-loading" />
  </div>
</template>

<script lang="ts" setup>
import { computed, defineEmits, defineProps, getCurrentInstance, ref, watch } from "vue";
import { useI18n } from "vue-i18n-bridge";
import { ThemeClient } from "@/lib/clients";
import { showOkCancelDialog } from "@/lib/dialog";
import { Dialog } from "@/lib/utils";
import { Novel } from "@/lib/models";
import type { NolaPfContestExtraFieldItem } from "@/lib/models/nolaPfContest";
import { extraFieldMaxLength } from "@/lib/utils/contestEntryExtraFields";
import { CONTEST_ENTRY_SYNOPSIS_MAX_LENGTH } from "@/lib/utils/contestEntryWorkForm";
import { readManuscriptFiles } from "@/lib/utils/manuscriptFileImport";
import { getNovelCoverImageSrc } from "@/lib/utils/contestEntryNovelSelection";
import ContestEntryNovelSelectDialog, {
  ContestEntryNovelSelectDialogResult,
} from "@/components/ui/dialogs/ContestEntryNovelSelectDialog.vue";
import ImportDialog, { ImportDialogResult } from "@/components/ui/dialogs/ImportDialog.vue";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/dialogs/SimpleDialog.vue";
import ImageView from "@/components/atoms/ImageView.vue";
import MaskedLoading from "@/components/atoms/MaskedLoading.vue";
import ContestEntryCopySynopsisSelect, {
  ContestEntryCopySynopsisSource,
} from "@/components/organisms/event/ContestEntryCopySynopsisSelect.vue";
import type {
  ContestEntryWorkCategoryOption,
  ContestEntryWorkFormValue,
} from "@/components/organisms/event/contestEntryWorkFormTypes";
import IconChevronDown from "~icons/figma/ChevronDown";
import IconClose from "~icons/figma/Close";
import IconNoteBook from "~icons/figma/NoteBook";
import IconUpload from "~icons/figma/Upload";

const novelSelectDialog = new Dialog(ContestEntryNovelSelectDialog);
const importDialog = new Dialog(ImportDialog);
const themeClient = new ThemeClient();

const props = defineProps<{
  value: ContestEntryWorkFormValue;
  categoryOptions: ContestEntryWorkCategoryOption[];
  canProceed: boolean;
  novels: Novel[];
  extraFieldItems: NolaPfContestExtraFieldItem[];
}>();

const emit = defineEmits<{
  (e: "input", value: ContestEntryWorkFormValue): void;
  (e: "back"): void;
  (e: "next"): void;
}>();

const { t } = useI18n();
const instance = getCurrentInstance();
const isImporting = ref(false);
const appliedSynopsis = ref<string | null>(null);

watch(
  () => props.value.novelId,
  () => {
    appliedSynopsis.value = null;
  }
);

const hasSelectedNolaWork = computed(() => props.value.workSource === "nola" && !!props.value.novelId);

const isSourceSelectDisabled = computed(() => props.categoryOptions.length > 0 && !props.value.categoryId);

function updateField<K extends keyof ContestEntryWorkFormValue>(field: K, fieldValue: ContestEntryWorkFormValue[K]) {
  emit("input", { ...props.value, [field]: fieldValue });
}

function onCategoryChange(event: Event) {
  const target = event.target as HTMLSelectElement;
  emit("input", {
    ...props.value,
    categoryId: target.value,
    novelId: "",
    novelTitle: "",
    novelImage: "",
    novelDescription: "",
    workTitle: "",
    synopsis: "",
    workSource: "",
  });
}

function showNotice(title: string, content: string, isError = false) {
  const dialog = new Dialog(SimpleDialog);
  const data: SimpleDialogProps = {
    title,
    content,
    isError,
    width: 350,
  };
  dialog.show(data);
}

function selectNolaWork(work: { novelId: string; title: string; image: string; description: string }) {
  appliedSynopsis.value = null;
  emit("input", {
    ...props.value,
    workSource: "nola",
    novelId: work.novelId,
    novelTitle: work.title,
    novelImage: work.image,
    novelDescription: work.description,
    workTitle: work.title,
    synopsis: "",
  });
}

function selectCreatedNovel(novelId: string, fallbackTitle: string) {
  const novels = (instance!.proxy.$store.getters["novelModule/novels"] as Novel[]) ?? [];
  const novel = novels.find((item) => item.novelId === novelId);
  const title = novel?.title?.trim() || fallbackTitle.trim() || (t("common.untitled") as string);

  selectNolaWork({
    novelId,
    title,
    image: novel ? getNovelCoverImageSrc(novel) : "",
    description: novel?.description ?? "",
  });
}

async function onClickImportFromLocal() {
  if (props.categoryOptions.length > 0 && !props.value.categoryId) return;

  const result = (await importDialog.show({ hideScriptMode: true })) as ImportDialogResult | null;
  if (!result) return;

  isImporting.value = true;
  try {
    const payloads = await readManuscriptFiles(result.files, false);

    const created = (await instance!.proxy.$store.dispatch("novelModule/createNovel", {
      title: result.title,
      manuscriptMode: "default",
    })) as Novel;

    await instance!.proxy.$store.dispatch("manuscriptModule/createMultipleManuscript", {
      params: payloads.map((item) => ({
        novelId: created.novelId,
        title: item.title || "",
        content: item.content || "",
      })),
    });
    await instance!.proxy.$store.dispatch("novelModule/initialize");

    selectCreatedNovel(created.novelId, result.title);
  } catch (error) {
    console.error(error);
    showNotice(
      t("homePage.novelList.import.errorTitle") as string,
      t("homePage.novelList.import.errorMessage") as string,
      true
    );
  } finally {
    isImporting.value = false;
  }
}

async function onClickSelectFromNola() {
  if (props.categoryOptions.length > 0 && !props.value.categoryId) return;

  const result = (await novelSelectDialog.show({
    title: t("contestEntryForm.step2.novelSelect.title"),
    novels: props.novels,
    initialNovelId: props.value.novelId,
  })) as ContestEntryNovelSelectDialogResult | null;

  if (!result) return;

  selectNolaWork({
    novelId: result.novelId,
    title: result.title,
    image: result.image,
    description: result.description,
  });
}

function clearSelectedWork() {
  emit("input", {
    ...props.value,
    workSource: "",
    novelId: "",
    novelTitle: "",
    novelImage: "",
    novelDescription: "",
    workTitle: "",
    synopsis: "",
  });
}

function onWorkTitleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  updateField("workTitle", target.value);
}

function onSynopsisInput(event: Event) {
  const target = event.target as HTMLTextAreaElement;
  updateField("synopsis", target.value);
}

function applySynopsis(text: string) {
  const synopsis = text.slice(0, CONTEST_ENTRY_SYNOPSIS_MAX_LENGTH);
  appliedSynopsis.value = synopsis;
  updateField("synopsis", synopsis);
}

function showUnsetSynopsisNotice(messageKey: "novelEmpty" | "themeEmpty") {
  showNotice(t("common.confirmTitle") as string, t(`contestEntryForm.step2.copySynopsis.${messageKey}`) as string);
}

async function resolveCopySynopsis(source: ContestEntryCopySynopsisSource): Promise<string | null> {
  if (source === "fromNovel") {
    const novelSynopsis = props.value.novelDescription ?? "";
    if (!novelSynopsis.trim()) {
      showUnsetSynopsisNotice("novelEmpty");
      return null;
    }
    return novelSynopsis;
  }

  try {
    const theme = await themeClient.fetchTheme(props.value.novelId);
    const themeSynopsis = theme?.detail?.find((detail) => detail.name === "synopsis")?.value ?? "";
    if (!themeSynopsis.trim()) {
      showUnsetSynopsisNotice("themeEmpty");
      return null;
    }
    return themeSynopsis;
  } catch (error) {
    console.error(error);
    showNotice(
      t("contestEntryForm.step2.submissionWork") as string,
      t("contestEntryForm.step2.copySynopsis.themeLoadError") as string,
      true
    );
    return null;
  }
}

async function onCopySynopsisSelect(source: ContestEntryCopySynopsisSource) {
  const synopsis = await resolveCopySynopsis(source);
  if (synopsis === null) return;

  const hasManualSynopsis = props.value.synopsis.trim().length > 0 && props.value.synopsis !== appliedSynopsis.value;
  if (hasManualSynopsis) {
    const confirmed = await showOkCancelDialog({
      title: t("contestEntryForm.step2.copySynopsis.overwriteConfirm.title") as string,
      content: t("contestEntryForm.step2.copySynopsis.overwriteConfirm.content") as string,
      okButton: t("contestEntryForm.step2.copySynopsis.overwriteConfirm.ok") as string,
      cancelButton: t("contestEntryForm.step2.copySynopsis.overwriteConfirm.cancel") as string,
      okVariant: "primary",
    });
    if (!confirmed) return;
  }

  applySynopsis(synopsis);
}

function extraAnswer(key: string): string {
  return props.value.extraAnswers?.[key] ?? "";
}

function onExtraAnswerInput(key: string, event: Event) {
  const target = event.target as HTMLInputElement;
  emit("input", {
    ...props.value,
    extraAnswers: {
      ...props.value.extraAnswers,
      [key]: target.value,
    },
  });
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.work-select-form {
  display: flex;
  flex-direction: column;
}

.field {
  margin-bottom: 24px;
}

.field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.field-label {
  display: block;
  margin: 0 0 4px 4px;
  font-size: var(--font-size-xs);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.field-header .field-label {
  margin-bottom: 0;
}

.required-mark {
  color: var(--color-warning);
}

.field-count {
  font-size: var(--font-size-xs);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.field-description {
  margin: 0 0 4px 4px;
  font-size: var(--font-size-xs);
  line-height: var(--line-height-default);
  color: var(--color-text-low);
}

.extra-fields {
  margin-top: 24px;
}

.field-input {
  display: block;
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  border: none;
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  line-height: var(--line-height-default);
  box-sizing: border-box;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.field-textarea {
  min-height: 120px;
  resize: vertical;
}

.field-select {
  appearance: none;
  height: 40px;
  padding-right: 32px;
}

.select-wrapper {
  position: relative;
}

.select-chevron {
  position: absolute;
  top: 50%;
  right: 12px;
  font-size: 12px;
  color: var(--color-text-normal);
  pointer-events: none;
  transform: translateY(-50%);
}

.copy-synopsis-field {
  margin-top: -8px;
  margin-bottom: 0;
}

.submission-section {
  margin-top: 0;
}

.submission-title {
  margin: 0 0 16px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
}

.selected-work-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 91px;
  margin-bottom: 24px;
  padding: 8px 40px 8px 16px;
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-layer);
}

.selected-work-cover {
  flex-shrink: 0;
  width: 40px;
  height: 59px;

  :deep(.image) {
    width: 40px;
    height: 59px;
    object-fit: cover;
  }
}

.selected-work-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  word-break: break-word;
}

.clear-work-button {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
    border-radius: var(--radius-xs);
  }
}

.clear-work-icon {
  font-size: 12px;
}

.synopsis-field {
  margin-bottom: 16px;
}

.source-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.source-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 128px;
  padding: 24px 16px;
  border: 2px solid transparent;
  border-radius: var(--radius-lg);
  background-color: var(--color-surface-layer);
  color: var(--color-text-normal);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &.selected {
    border-color: var(--color-primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.44;

    &:hover::after {
      background: transparent;
    }
  }
}

.source-icon {
  font-size: 40px;
  color: var(--color-text-normal);
}

.source-label {
  font-size: var(--font-size-md);
  line-height: var(--line-height-default);
  text-align: center;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 40px;
}

.back-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.next-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.44;
  }
}

.import-loading {
  z-index: 100;
}

@include mq("sp") {
  .source-cards {
    grid-template-columns: 1fr;
  }

  .form-footer {
    margin-top: 24px;
  }
}
</style>
